import { motion, AnimatePresence } from "framer-motion";
import { 
  Smartphone, 
  Monitor, 
  FileText, 
  CreditCard, 
  Send,
  Clock,
  ThumbsUp,
  Zap,
  MessageSquare
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/modern_digital_hospital_check-in_experience.png";

export default function EncounterManagementCard() {
  const features = [
    {
      icon: Smartphone,
      title: "Digital-First Check-In",
      desc: "Kiosk, mobile, QR, or web—patients choose how they arrive"
    },
    {
      icon: Monitor,
      title: "Real-Time Status Boards",
      desc: "Waiting room displays and staff views updated as patients progress"
    },
    {
      icon: FileText,
      title: "Automated Visit Capture",
      desc: "Documentation and coding happen seamlessly in the background"
    },
    {
      icon: CreditCard,
      title: "Streamlined Checkout",
      desc: "Copay collection, receipt delivery, and follow-up scheduling in one flow"
    },
    {
      icon: Send,
      title: "Post-Visit Engagement",
      desc: "Summaries, surveys, and care instructions sent via SMS, email, or portal"
    }
  ];

  const kpis = [
    { label: "Check-In Time", value: "-70%", icon: Clock, color: "text-emerald-400" },
    { label: "Patient Satisfaction", value: "+35%", icon: ThumbsUp, color: "text-blue-400" },
    { label: "Checkout Speed", value: "-50%", icon: Zap, color: "text-purple-400" },
    { label: "Survey Response Rate", value: "+60%", icon: MessageSquare, color: "text-amber-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-10 flex flex-col justify-start relative z-10 overflow-y-auto">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Live Encounter
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            Seamless Encounter
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            From Check-In to Checkout, Frictionless
          </h2>
          
          <p className="text-xl text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Every encounter touchpoint orchestrated for speed, accuracy, and patient satisfaction."
          </p>

          <div className="space-y-5">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer: Abstract Flow */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Floating Abstract UI Elements - Randomly Scattered */}
          <div className="relative z-10 w-full h-full pointer-events-none">
             
             {/* Card 1: Check-in */}
             <motion.div
               animate={{ 
                 y: [0, -10, 0],
                 x: [0, 8, 0]
               }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
               className="absolute top-[4%] left-[12%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <div className="w-4 h-4 rounded-full bg-green-400 animate-pulse" />
                   <span className="text-lg text-white font-bold uppercase">Check-In</span>
                </div>
                <div className="h-4 w-48 bg-white/10 rounded-full overflow-hidden">
                   <div className="h-full bg-green-400 w-full" />
                </div>
                <div className="text-base text-white/70 mt-2">Digital Intake Complete</div>
             </motion.div>

             {/* Card 2: Copay */}
             <motion.div
               animate={{ 
                 y: [0, 12, 0],
                 x: [0, -6, 0]
               }}
               transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
               className="absolute top-[8%] right-[8%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <CreditCard className="w-8 h-8 text-blue-400" />
                   <span className="text-lg text-white font-bold uppercase">Copay</span>
                </div>
                <div className="text-4xl font-bold text-white">$25.00</div>
                <div className="text-base text-white/70">Processed Automatically</div>
             </motion.div>

             {/* Card 3: No Wait Time */}
             <motion.div
               animate={{ 
                 y: [0, -8, 0],
                 x: [0, 10, 0]
               }}
               transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
               className="absolute top-[28%] left-[5%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <Zap className="w-8 h-8 text-amber-400" />
                   <span className="text-lg text-white font-bold uppercase">No Wait</span>
                </div>
                <div className="flex items-baseline gap-2">
                   <span className="text-4xl font-bold text-white">0</span>
                   <span className="text-lg text-white/70">min</span>
                </div>
                <div className="text-base text-white/70 mt-2">Direct Rooming</div>
             </motion.div>

             {/* Card 4: Value-Based Care */}
             <motion.div
               animate={{ 
                 y: [0, 15, 0],
                 x: [0, -8, 0]
               }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               className="absolute top-[22%] right-[15%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <Monitor className="w-8 h-8 text-emerald-400" />
                   <span className="text-lg text-white font-bold uppercase">Value-Based</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                   <div className="px-3 py-1 rounded bg-blue-500/20 text-blue-300 text-base font-bold">Quality</div>
                   <div className="px-3 py-1 rounded bg-emerald-500/20 text-emerald-300 text-base font-bold">Outcomes</div>
                </div>
             </motion.div>

             {/* Card 5: Patient Centric */}
             <motion.div
               animate={{ 
                 y: [0, 10, 0],
                 x: [0, -6, 0]
               }}
               transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
               className="absolute top-[48%] left-[18%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <ThumbsUp className="w-8 h-8 text-pink-400" />
                   <span className="text-lg text-white font-bold uppercase">Patient Centric</span>
                </div>
                 <div className="flex -space-x-2 mb-2">
                   <div className="w-10 h-10 rounded-full bg-slate-700 border border-white/20 flex items-center justify-center text-base text-white">PX</div>
                   <div className="w-10 h-10 rounded-full bg-slate-700 border border-white/20 flex items-center justify-center text-base text-white">❤️</div>
                </div>
                <div className="text-base text-white/70">Personalized Journey</div>
             </motion.div>

             {/* Card 6: Post-Visit */}
             <motion.div
               animate={{ 
                 y: [0, -12, 0],
                 x: [0, 7, 0]
               }}
               transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
               className="absolute top-[42%] right-[6%] bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-xl"
             >
                <div className="flex items-center gap-3 mb-3">
                   <Send className="w-8 h-8 text-purple-400" />
                   <span className="text-lg text-white font-bold uppercase">Post-Visit</span>
                </div>
                <div className="flex -space-x-2">
                   <div className="w-10 h-10 rounded-full bg-slate-700 border border-white/20 flex items-center justify-center text-base text-white">RX</div>
                   <div className="w-10 h-10 rounded-full bg-slate-700 border border-white/20 flex items-center justify-center text-base text-white">DOC</div>
                </div>
                <div className="text-base text-white/70 mt-2">Sent to Portal</div>
             </motion.div>

          </div>


          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

